import {
  Server,
  BarChart3,
  BookOpen,
  Banknote,
  Building2,
  CalendarClock,
  CalendarDays,
  Clock,
  FileSignature,
  FolderKanban,
  Handshake,
  Home,
  KeyRound,
  PackageCheck,
  Radar, // Phase M (m-ui)
  KeySquare,
  Landmark,
  LifeBuoy,
  Receipt,
  ReceiptText,
  Settings,
  ShieldCheck,
  Sparkles,
  UserRound,
  Users,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import type { Gated } from '@/modules/subscription/api';

/** A sidebar link. `Gated` holds permission, planModule (Phase I, I-B), needsEmployee and platformOnly (I-D). */
export interface NavItem extends Gated {
  label: string;
  to: string;
  icon: LucideIcon;
  /** P2-0 (N7): sub-pages the command palette offers as "Module › Page". */
  pages?: NavPage[];
}

export interface NavPage extends Gated {
  label: string;
  to: string;
}

export interface NavGroup {
  section?: string;
  items: NavItem[];
}

/**
 * Main sidebar, grouped as approved in the November 2026 platform review (section 6, P2-0).
 * Items the user can't see are hidden (`useCanSee`: permission, plan module, employee link,
 * platform admin); the API enforces it anyway. The command palette reads the same list.
 */
export const NAV: NavGroup[] = [
  {
    items: [
      { label: 'Home', to: '/', icon: Home },
      {
        label: 'My Space',
        to: '/me',
        icon: UserRound,
        needsEmployee: true,
        planModule: 'hr',
        pages: [
          { label: 'Leave', to: '/me/leave' },
          { label: 'Timesheets', to: '/me/timesheets' },
          { label: 'Expenses', to: '/me/expenses' },
          { label: 'Shifts & clock', to: '/me/shifts' },
          { label: 'Payroll', to: '/me/payroll', planModule: 'payroll' },
        ],
      },
    ],
  },
  {
    section: 'Service',
    items: [
      {
        label: 'Service desk',
        to: '/tickets',
        icon: LifeBuoy,
        permission: 'tickets.read',
        pages: [
          { label: 'Board', to: '/tickets/board' },
          { label: 'Approvals', to: '/tickets/approvals' },
          { label: 'SLA compliance', to: '/reports/sla', permission: 'reports.read' }, // P2-4 (BUG-59): moved to Reports
          { label: 'Settings', to: '/tickets/settings', permission: 'tickets.admin' },
        ],
      },
      { label: 'Schedule', to: '/schedule', icon: CalendarDays, permission: 'schedule.read' },
      { label: 'Time', to: '/time', icon: Clock, permission: 'time.read' },
      {
        label: 'Monitoring',
        to: '/monitoring',
        icon: Radar,
        permission: 'monitoring.read',
        planModule: 'monitoring', // Phase M (m-ui): Denwen MSP Monitor
        pages: [
          { label: 'Alerts', to: '/monitoring/alerts' },
          { label: 'Maintenance', to: '/monitoring/maintenance' },
          { label: 'Status pages', to: '/monitoring/status-pages' },
        ],
      },
    ],
  },
  {
    section: 'Clients',
    items: [
      { label: 'Organizations', to: '/organizations', icon: Building2, permission: 'organizations.read' },
      { label: 'Contacts', to: '/contacts', icon: Users, permission: 'organizations.read' },
      {
        label: 'Sales',
        to: '/crm',
        icon: Handshake,
        permission: 'crm.read', // Phase D CRM track (D8); Track 4: crm.*
        pages: [
          { label: 'Deals', to: '/crm/deals' },
          { label: 'Tasks', to: '/crm/tasks' },
          { label: 'Quotes', to: '/billing/quotes', permission: 'billing.read' },
        ],
      },
    ],
  },
  {
    // P2-0 (N4): the documentation tools have their own group (they were under Clients).
    section: 'Documentation',
    items: [
      {
        label: 'Passwords',
        to: '/passwords',
        icon: KeyRound,
        permission: 'passwords.read', // B1 (vault track)
        pages: [
          { label: 'My Vault', to: '/passwords/my-vault' },
          { label: 'Keys & certificates', to: '/passwords/keys' },
          { label: 'Password health', to: '/passwords/health' }, // P3 (D9)
          { label: 'Break-glass access', to: '/passwords/break-glass' }, // P3 (D9)
        ],
      },
      { label: 'Configurations', to: '/configurations', icon: Server, permission: 'docs.read' }, // P3 (D8): across organizations
      { label: 'Knowledge base', to: '/knowledge', icon: BookOpen, permission: 'docs.read' },
      { label: 'Ask the docs', to: '/ask', icon: Sparkles, permission: 'docs.read' }, // Phase F (F5): AI documentation Q&A
      { label: 'Expiring', to: '/passwords/expiring', icon: CalendarClock, permission: 'docs.read' }, // M14 (track 5)
    ],
  },
  {
    // P2-0 (N3): Delivery keeps the work; the money moved to Finance.
    section: 'Delivery',
    items: [
      {
        label: 'Projects',
        to: '/projects',
        icon: FolderKanban,
        permission: 'projects.read',
        pages: [
          { label: 'My tasks', to: '/projects/my-tasks' },
          { label: 'Workload', to: '/projects/workload' },
          { label: 'Templates', to: '/projects/templates' },
        ],
      },
      { label: 'Contracts', to: '/agreements', icon: FileSignature, permission: 'agreements.read' },
      { label: 'Purchasing', to: '/purchasing', icon: PackageCheck, permission: 'purchasing.read', pages: [{ label: 'Stock', to: '/purchasing/stock' }] }, // Phase D CRM track (D11); Track 4
      { label: 'Licences', to: '/licences', icon: KeySquare, permission: 'billing.read' }, // Phase D CRM track (D10)
    ],
  },
  {
    section: 'Finance',
    items: [
      {
        label: 'Billing',
        to: '/billing',
        icon: Receipt,
        permission: 'billing.read',
        pages: [
          { label: 'Invoices', to: '/billing/invoices' },
          { label: 'Credit notes', to: '/billing/credit-notes' },
          { label: 'Quotes', to: '/billing/quotes' },
          { label: 'Billing runs', to: '/billing/runs' },
          { label: 'Aged receivables', to: '/billing/receivables' },
          { label: 'Unbilled', to: '/billing/unbilled' },
          { label: 'Products & services', to: '/billing/products' },
          { label: 'Rebillable costs', to: '/billing/expenses' }, // P2-0: owner decision 5 (was "Expenses")
        ],
      },
      {
        label: 'Bills',
        to: '/payables',
        icon: ReceiptText,
        permission: 'accounting.read',
        planModule: 'accounting', // G2 (payables)
        pages: [
          { label: 'Payments', to: '/payables/payments' },
          { label: 'Repeating bills', to: '/payables/repeating' },
          { label: 'Aged payables', to: '/payables/aged' },
        ],
      },
      { label: 'Banking', to: '/banking', icon: Banknote, permission: 'accounting.read', planModule: 'accounting', pages: [{ label: 'Bank rules', to: '/banking/rules' }] }, // G3/G4 (g-banking)
      {
        label: 'Accounting',
        to: '/accounting',
        icon: Landmark,
        permission: 'accounting.read',
        planModule: 'accounting', // G1 (ledger core)
        pages: [
          { label: 'Chart of accounts', to: '/accounting/accounts' },
          { label: 'Financial reports', to: '/accounting/reports' },
          { label: 'VAT', to: '/accounting/vat' },
          { label: 'Year end', to: '/accounting/year-end' },
        ],
      },
    ],
  },
  {
    section: 'People',
    items: [
      {
        label: 'HR',
        to: '/hr',
        icon: UserRound,
        permission: 'hr.read',
        planModule: 'hr',
        pages: [
          { label: 'People', to: '/hr/people' },
          { label: 'Leave', to: '/hr/leave' },
          { label: 'Timesheets', to: '/hr/timesheets' },
          { label: 'Expenses', to: '/hr/expenses' },
          { label: 'Sickness', to: '/hr/sickness' },
          { label: 'Rota', to: '/hr/rota' },
          { label: 'Recruitment', to: '/hr/recruitment' },
          { label: 'Statutory pay', to: '/payroll/statutory-pay', permission: 'hr.sensitive', planModule: 'payroll' }, // P2-4: lives in Payroll
        ],
      },
      {
        label: 'Payroll',
        to: '/payroll',
        icon: Banknote,
        permission: 'payroll.read',
        planModule: 'payroll', // Phase H: UK payroll
        pages: [
          { label: 'Pay runs', to: '/payroll/runs' },
          { label: 'Employees', to: '/payroll/employees' },
          { label: 'HMRC (RTI)', to: '/payroll/rti' },
          { label: 'Pensions', to: '/payroll/pensions' }, // P2-4 (BUG-31)
        ],
      },
    ],
  },
  {
    section: 'Insights',
    items: [
      {
        label: 'Reports',
        to: '/reports',
        icon: BarChart3,
        permission: 'reports.read',
        pages: [
          { label: 'Report builder', to: '/reports/builder' },
          { label: 'Scheduled reports', to: '/reports/schedules' },
        ],
      },
    ],
  },
  {
    section: 'Admin',
    items: [
      { label: 'Automation', to: '/automation', icon: Workflow, permission: 'automation.read', pages: [{ label: 'Runs', to: '/automation/runs' }] },
      {
        label: 'Settings',
        to: '/admin',
        icon: Settings,
        permission: 'admin.read',
        pages: [
          { label: 'Roles & permissions', to: '/admin/roles' },
          { label: 'Security', to: '/admin/security' },
          { label: 'Audit log', to: '/admin/audit' },
          { label: 'Branding', to: '/admin/branding' },
          { label: 'Email', to: '/admin/email' },
          { label: 'Custom fields', to: '/admin/custom-fields' },
          { label: 'Modules', to: '/admin/modules' }, // P2-4 (N11)
          { label: 'Denwen RMM', to: '/admin/denwen-rmm', permission: 'integrations.read' },
          { label: 'Subscription', to: '/admin/subscription' },
        ],
      },
      { label: 'Platform', to: '/platform', icon: ShieldCheck, platformOnly: true }, // Phase I (I-D): the platform console
    ],
  },
];
